<template>
    <li
        class="sbs-draggable-item"
        v-bind="itemData">
        <div class="sbs-draggable-item__head">
            <div class="sbs-draggable-item__offset-panel">
                <SbsButtonIcon
                    class="sbs-draggable-item__folder-button"
                    v-bind="accordionButtonData"
                    @click.capture="folderButtonClickHandler">
                    <!-- @slot Слот для иконки кнопки аккордиона -->
                    <slot name="accordionButtonIcon"></slot>
                </SbsButtonIcon>

                <span
                    class="sbs-draggable-item__icon-wrapper draghandle"
                    v-bind="iconWrapperData">
                    <!-- @slot Слот для иконки типа элемента -->
                    <slot name="itemTypeIcon">
                        <SbsIcon
                            class="sbs-draggable-item__icon sbs-draggable-item__icon--type"
                            v-bind="itemIconData" />
                        <SbsIcon
                            class="sbs-draggable-item__icon sbs-draggable-item__icon--drag"
                            v-bind="itemHoverIconData" />
                    </slot>
                </span>
            </div>

            <!-- @slot Слот по-умолчанию для контента элемента драглиста -->
            <slot
                v-bind="{
                    index,
                    item,
                }">
            </slot>
        </div>

        <div
            v-if="isFolder"
            class="sbs-draggable-item__body"
            v-bind="itemBodyData">
            <div class="sbs-draggable-item__decor-border-wrapper">
                <div class="sbs-draggable-item__decor-border"></div>
            </div>
            <SbsDraggableList
                v-bind="listData"
                class="sbs-draggable-item__list"
                @pointerenter.prevent="listPointerenterHandler"
                @pointerleave.prevent="listPointerleaveHandler"
                @start="dragStartHandler"
                @end="dragEndHandler">
                <template #accordionButtonIcon>
                    <!-- @slot Слот для иконки кнопки аккордеона -->
                    <slot name="accordionButtonIcon"></slot>
                </template>

                <template #itemTypeIcon>
                    <!-- @slot Слот для иконки типа элемента -->
                    <slot name="itemTypeIcon"></slot>
                </template>

                <template #default="slotProps">
                    <!-- @slot Слот по-умолчанию -->
                    <slot v-bind="slotProps"></slot>
                </template>
            </SbsDraggableList>
        </div>
    </li>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';
import { AllSizes } from '../../utils/sizes.js';
import { AllIconGroups } from '../icon/utils/consts.js';

/**
* Элемент списка драглиста
*/
export default {
    name: 'SbsDraggableItem',
    mixins: [themesMixin],
    data() {
        return {
            isDropEnabled: null,
            isFolderOpened: true,
            listOpenTimer: null,
        };
    },
    computed: {
        accordionButtonData() {
            return {
                'colorTheme': AllColors.BLACK,
                'data-empty': String(this.isFolderEmpty),
                'data-opened': String(this.isFolderOpened),
                'iconGroup': AllIconGroups.SOLID,
                'iconName': 'triangle',
                'iconSize': AllSizes.X_SMALL,
            };
        },
        iconWrapperData() {
            return {
                class: {
                    'while-dragging': this.whileDragging,
                },
            };
        },

        isFolder() {
            return this.item.type === 'folder';
        },
        isFolderEmpty() {
            const hasNoItems = !this.item.items?.length;

            return this.isFolder && hasNoItems;
        },
        itemBodyData() {
            return {
                'data-empty': String(this.isFolderEmpty),
                'data-level': this.item.level,
                'data-opened': String(this.isFolderOpened),
            };
        },
        itemData() {
            return {
                'class': {
                    'while-dragging': this.whileDragging,
                },
                'data-depth': this.item.depth,
                'data-level': this.item.level,
                'data-opened': String(this.isFolderOpened),
                'data-type': this.item.type,
            };
        },
        itemIconColorResolver() {
            switch (this.item.type) {
                case 'folder': return AllColors.YELLOW;
                case 'page': return AllColors.GREEN;
                default: return AllColors.YELLOW;
            }
        },
        itemIconData() {
            return {
                'class': this.item.type,
                'colorTheme': this.itemIconColorResolver,
                'data-type': this.item.type,
                'iconGroup': AllIconGroups.SOLID,
                'iconName': this.itemIconNameResolver,
            };
        },
        itemIconNameResolver() {
            switch (this.item.type) {
                case 'folder': return 'folder';
                case 'page': return 'file-alt';
                default: return 'question';
            }
        },
        itemHoverIconData() {
            return {
                backgroundTheme: AllColors.LIGHTBLUE,
                colorTheme: AllColors.BLUE,
                iconGroup: AllIconGroups.SOLID,
                iconName: 'arrows',
            };
        },
        listData() {
            return {
                'class': {
                    'while-dragging': this.whileDragging,
                },
                'data-drop': String(this.isDropEnabled && this.whileDragging),
                'data-empty': String(this.isFolderEmpty),
                'data-opened': String(this.isFolderOpened),
                'dragClass': 'sbs-draggable-item--drag',
                'forceFallback': true,
                'ghostClass': 'sbs-draggable-item--ghost',
                'isOuterItemDragging': this.whileDragging,
                'level': this.item.level,
                'maxNestedDepth': this.maxNestedDepth,
                'outerDraggedItem': this.draggedItem,
                'modelValue': this.item.items,
            };
        },
    },
    methods: {
        dragEndHandler(evt) {
            /**
             * Эмит события `end`. Происходит при завершении перетаскивания элемента.
             */
            this.$emit('end', evt);
        },
        dragStartHandler(evt) {
            /**
             * Эмит события `start`. Происходит при начале перетаскивания элемента.
             */
            this.$emit('start', evt);
        },
        folderButtonClickHandler() {
            this.isFolderOpened = !this.isFolderOpened;
        },
        listPointerenterHandler() {
            const FOLDER_TOGGLING_TIMEOUT = 1000;
            const timeout = this.isFolderEmpty ? 0 : FOLDER_TOGGLING_TIMEOUT;

            clearTimeout(this.listOpenTimer);
            this.listOpenTimer = setTimeout(() => {
                this.isFolderOpened = true;
            }, timeout);
        },
        listPointerleaveHandler() {
            clearTimeout(this.listOpenTimer);
        },
    },
    props: {
        /**
         * Перемещаемый в данное время элемент
         */
        draggedItem: {
            type: HTMLElement,
            default: () => ({}),
        },
        /**
         * Индекс элемента
         */
        index: {
            type: Number,
            default: null,
        },
        /**
         * Объект элемента
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Максимальный уровень вложенности папки (data-type="folder")
         * N < 0 — вложенность без ограничений
         * 0 — нельзя вкладывать папки в другие папки
         * N > 0 — разрешено N уровеней вложенности
         */
        maxNestedDepth: {
            type: Number,
            default: -1,
        },
        /**
         * В значении True, когда пользователь схватил и тянет элемент
         */
        whileDragging: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        draggedItem(val) {
            if (!val || val === this.$el) return;

            const draggedItemDepth = val.dataset.depth;
            const isDraggedFolder = val.dataset.type === 'folder';
            const targetItemLevel = this.item.level;

            if (!isDraggedFolder) {
                this.isDropEnabled = true;
            } else if (this.maxNestedDepth < 0) {
                this.isDropEnabled = true;
            } else {
                this.isDropEnabled = draggedItemDepth + targetItemLevel < this.maxNestedDepth;
            }
        },
    },
};
</script>

<style lang="scss">
$item-height: 70px;
$root-item-height: 70px;
$nested-item-height: 60px;
$accordion-button-width: 30px;
$type-icon-width: 40px;
$offset-panel-left-padding: 25px;
$offset-panel-right-padding: 15px;
$offset-panel-width:
    calc(
        #{$accordion-button-width}
        + #{$type-icon-width}
        + #{$offset-panel-left-padding}
        + #{$offset-panel-right-padding}
    );
$nested-item-offset:
    calc(
        #{$offset-panel-left-padding}
        + #{$accordion-button-width}
    );
$decor-border-height: calc(100% - #{$nested-item-height} / 2);
$decor-border-div-width:
    calc(#{$accordion-button-width} / 2);

.sbs-draggable-item {
    position: relative;
    display: block;

    &--ghost {
        opacity: 0.6;
    }

    &--drag {
        width: 300px !important; // stylelint-disable-line declaration-no-important
        height: $nested-item-height !important; // stylelint-disable-line declaration-no-important
        overflow: hidden;
        background-color: $white;
        border: 1px solid $gray;
        border-radius: $rounded-15;

        &[data-level="0"] {
            height: $root-item-height !important; // stylelint-disable-line declaration-no-important
        }

        &::before {
            display: none;
        }

        .sbs-draggable-item__icon {
            &--type {
                opacity: 0;
            }

            &--drag {
                opacity: 1;
            }
        }

        .sbs-draggable-item__head {
            padding-right: 20px;
        }

        .sbs-draggable-item__table {
            display: none;
        }
    }

    &:not([data-level="0"]) {
        border-radius: $rounded-15;

        &::before {
            position: absolute;
            top: calc(0px - #{$nested-item-height} / 2);
            left: calc(0px - #{$decor-border-div-width});
            width: $decor-border-div-width;
            height: $nested-item-height;
            border-left: 1px solid $gray;
            border-bottom-left-radius: $rounded-15;
            content: "";
        }

        &:first-child::before {
            top: 0;
            height: calc(#{$nested-item-height} / 2);
        }
    }

    &[data-level="0"] {
        border-top: 1px solid $gray-light;

        &:first-child {
            border-top: none;
        }
    }

    &.while-dragging {
        border-top-color: $transparent;
    }

    &__list {
        width: calc(100% - #{$nested-item-offset});
        transition: $trs;

        &::before {
            position: absolute;
            top: 0;
            left: calc(#{$offset-panel-width} - 7px);
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            width: calc(100% - #{$offset-panel-width} + 7px);
            height: $nested-item-height;
            color: $primary;
            border: 1px dashed $transparent;
            border-radius: $rounded-15;
            opacity: 0;
            content: "";
            pointer-events: none;

            @include typo-secondary-demi;
        }

        &::after {
            position: absolute;
            top: 0;
            left: $offset-panel-width;
            width: calc(100% - #{$offset-panel-width});
            height: $nested-item-height;
            opacity: 0;
            content: "";
            pointer-events: none;
        }

        &.while-dragging {
            &[data-drop="false"]::before {
                background-color: rgba($crimson, 0.1);
                border-color: $crimson;
                opacity: 1;
                content: "";
            }

            &[data-drop="true"]::before {
                background-color: rgba($white, 0.8);
                border-color: $primary;
                opacity: 1;
                content: "Отпустите, чтобы добавить в эту папку";
            }
        }

        &[data-level="0"]::before,
        &[data-level="0"]::after {
            height: $root-item-height;
        }

        .sbs-draggable-item[draggable="true"] & {
            &::before,
            &::after {
                content: none;
            }
        }

        &.while-dragging::after {
            opacity: 1;
            pointer-events: all;
        }
    }

    &__head {
        display: flex;
        flex-shrink: 0;
        width: 100%;
        height: $item-height;
        background-color: $gray-ultra-light;

        .sbs-draggable-item:not([data-level="0"]) & {
            height: $nested-item-height;
            background-color: $transparent;
        }

        .while-dragging.sbs-draggable-item & {
            border-radius: $rounded-15;
        }
    }

    &__offset-panel {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        padding-right: $offset-panel-right-padding;
        padding-left: $offset-panel-left-padding;
        background-color: $transparent;
        border: none;
    }

    &__folder-button {
        width: $accordion-button-width;
        height: 100%;
        transition: $trs;

        &[data-opened="false"][data-empty="false"] {
            transform: rotate(-180deg) translateY(0) scale(1);
            opacity: 1;
        }

        &[data-opened="true"][data-empty="false"] {
            transform: rotate(0) translateY(-2px);
            opacity: 1;
        }

        &[data-empty="true"],
        .sbs-draggable-item[data-type="page"] & {
            cursor: default;
            opacity: 0;
            pointer-events: none;
        }

        .sbs-icon.sbs-button-icon__icon {
            z-index: 0;
            font-size: 10px;
        }
    }

    &__icon-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: $type-icon-width;
        height: $type-icon-width;
        transition: $trs;

        &:hover {
            cursor: move;
        }
    }

    &__icon {
        width: 100%;
        height: 100%;
        border-radius: $rounded-circle;
        transition: $trs;

        &--type {
            z-index: 1;
            opacity: 1;

            .sbs-draggable-item__icon-wrapper:not(.while-dragging):hover & {
                opacity: 0;
            }

            &[data-type="page"] {
                background-color: rgba($green, 0.15);
            }

            &[data-type="folder"] {
                background-color: rgba($yellow, 0.15);
            }
        }

        &--drag {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 0;
            opacity: 0;

            .sbs-draggable-item__icon-wrapper:not(.while-dragging):hover & {
                opacity: 1;
            }
        }
    }

    &__body {
        display: flex;
        max-width: 100%;
        height: 0;
        overflow: hidden;
        transition: $trs;

        &[data-level="0"][data-empty="false"][data-opened="true"] {
            padding-top: 15px;
            padding-bottom: 15px;
        }

        &[data-opened="true"] {
            height: auto;
            opacity: 1;
        }
    }

    &__decor-border-wrapper {
        display: flex;
        flex-shrink: 0;
        justify-content: flex-end;
        width: $nested-item-offset;
        overflow: hidden;
    }

    &__decor-border {
        width: $decor-border-div-width;
        height: $decor-border-height;
        border-left: 1px solid $gray;
        border-bottom-left-radius: 15px;
    }
}
</style>
